import {ReactElement, useState} from "react";
import {Field, Input, InputGroup, Label} from "@zendeskgarden/react-forms";
import {Button, IconButton} from "@zendeskgarden/react-buttons";
import {ReactComponent as QuestionMarkIcon} from "@zendeskgarden/svg-icons/src/16/question-mark-fill.svg";
import {ReactComponent as MaximizeIcon} from "@zendeskgarden/svg-icons/src/12/maximize-stroke.svg";
import {Body, Close, Footer, FooterItem, Header, Modal} from "@zendeskgarden/react-modals";
import {Body as TBody, Cell, Head, HeaderCell, HeaderRow, Row, Table} from "@zendeskgarden/react-tables";
import * as css from "./styles/FormulaInput.module.scss";
import {AutocompleteTextarea, Keyword} from "./AutocompleteTextarea";
import {toast} from "react-toastify";

const FORMULA_KEYWORDS: Keyword[] = [
    {keyword: "previous_in_place_rent"},
    {keyword: "market_rent"},
    {keyword: "month_num"},
    {keyword: "month_name"},
    {keyword: "forecast_period"},
    {keyword: "is_loss_to_lease"},
    {keyword: "if", details: "if(cond, then, else)"},
    {keyword: "min", details: "min(a, b)"},
    {keyword: "max", details: "max(a, b)"},
    {keyword: "abs", details: "abs(x)"},
    {keyword: "ceil", details: "ceil(x)"},
    {keyword: "floor", details: "floor(x)"},
    {keyword: "round", details: "round(x)"},
    {keyword: "sqrt", details: "sqrt(x)"},
    {keyword: "log", details: "log(x)"},
    {keyword: "pow", details: "pow(a, b)"},
    {keyword: "exp", details: "exp(x)"},
    {keyword: "and"},
    {keyword: "or"},
    {keyword: "not"},
    {keyword: "true"},
    {keyword: "false"},
    {keyword: "\"JAN\""},
    {keyword: "\"FEB\""},
    {keyword: "\"MAR\""},
    {keyword: "\"APR\""},
    {keyword: "\"MAY\""},
    {keyword: "\"JUN\""},
    {keyword: "\"JUL\""},
    {keyword: "\"AUG\""},
    {keyword: "\"SEP\""},
    {keyword: "\"OCT\""},
    {keyword: "\"NOV\""},
    {keyword: "\"DEC\""},
    {keyword: "\"REFORECAST\""},
    {keyword: "\"BUDGET\""},
];

export type ValidateResult = {valid: boolean, error?: string | null};
export type ValidateFunction = (formula: string) => Promise<ValidateResult>;

export interface FormulaInputProps {
    disabled?: boolean;
    value: string;
    onChange: (evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    validateFormula?: ValidateFunction;
    placeholder?: string;
    className?: string;
    label?: ReactElement;
}

function FormulaHelpContent(): ReactElement {
    return (
        <>
            <h3 className={css.header}>Examples</h3>
            <Table size="small">
                <Head>
                    <HeaderRow>
                        <HeaderCell>Formula</HeaderCell>
                        <HeaderCell>Description</HeaderCell>
                    </HeaderRow>
                </Head>
                <TBody>
                    <Row>
                        <Cell><code>previous_in_place_rent * 0.03</code></Cell>
                        <Cell>An increase of 3% of the previous month's In Place Rent every month.</Cell>
                    </Row>
                    <Row>
                        <Cell><code><pre>{"if(\n  month_name == \"JUL\" and forecast_period == \"REFORECAST\",\n  previous_in_place_rent * 0.03,\n  0\n)"}</pre></code></Cell>
                        <Cell>Apply a 3% increase of previous month's In-Place Rent in July.</Cell>
                    </Row>
                    <Row>
                        <Cell><code><pre>{"min(\n  previous_in_place_rent * 0.05,\n  market_rent - previous_in_place_rent\n)"}</pre></code></Cell>
                        <Cell>Increase by 5% of previous In-Place Rent, but never exceed market rent.</Cell>
                    </Row>
                    <Row>
                        <Cell><code><pre>{"if(\n  forecast_period == \"BUDGET\",\n  previous_in_place_rent * 0.04,\n  previous_in_place_rent * 0.02\n)"}</pre></code></Cell>
                        <Cell>Apply a 4% increase during Budget periods and a 2% increase during Reforecast periods.</Cell>
                    </Row>
                </TBody>
            </Table>
            <h3 className={css.header}>Variables</h3>
            <Table size="small">
                <Head>
                    <HeaderRow>
                        <HeaderCell>Variable</HeaderCell>
                        <HeaderCell>Description</HeaderCell>
                    </HeaderRow>
                </Head>
                <TBody>
                    <Row><Cell><code>previous_in_place_rent</code></Cell><Cell>Previous month's in-place rent for the unit-type</Cell></Row>
                    <Row><Cell><code>market_rent</code></Cell><Cell>Current month's market rent for the unit-type</Cell></Row>
                    <Row><Cell><code>month_num</code></Cell><Cell>The current month number (e.g. 1 = January, 2 = February, ...).</Cell></Row>
                    <Row><Cell><code>month_name</code></Cell><Cell>The current month abbreviation (e.g. "JAN" = January, "FEB" = February, ...).</Cell></Row>
                    <Row><Cell><code>forecast_period</code></Cell><Cell>One of "REFORECAST" or "BUDGET"</Cell></Row>
                    <Row><Cell><code>is_loss_to_lease</code></Cell><Cell>A boolean value that is <code>true</code> if <code>market_rent &gt;= previous_in_place_rent</code>. Useful for <code>if(is_loss_to_lease, do some calc if loss to lease, do some calc if gain to lease)</code></Cell></Row>
                </TBody>
            </Table>

            <h3 className={css.header}>Functions</h3>
            <Table size="small">
                <Head>
                    <HeaderRow>
                        <HeaderCell>Function</HeaderCell>
                        <HeaderCell>Description</HeaderCell>
                    </HeaderRow>
                </Head>
                <TBody>
                    <Row><Cell><code>if(cond, then, else)</code></Cell><Cell>Returns <em>then</em> if condition is true, otherwise <em>else</em></Cell></Row>
                    <Row><Cell><code>min(a, b)</code></Cell><Cell>Returns the smaller of two values</Cell></Row>
                    <Row><Cell><code>max(a, b)</code></Cell><Cell>Returns the larger of two values</Cell></Row>
                    <Row><Cell><code>abs(x)</code></Cell><Cell>Absolute value</Cell></Row>
                    <Row><Cell><code>ceil(x)</code></Cell><Cell>Round up to nearest integer</Cell></Row>
                    <Row><Cell><code>floor(x)</code></Cell><Cell>Round down to nearest integer</Cell></Row>
                    <Row><Cell><code>round(x)</code></Cell><Cell>Round to nearest integer</Cell></Row>
                    <Row><Cell><code>sqrt(x)</code></Cell><Cell>Square root</Cell></Row>
                    <Row><Cell><code>log(x)</code></Cell><Cell>Natural logarithm</Cell></Row>
                    <Row><Cell><code>pow(a, b)</code></Cell><Cell>Raise <em>a</em> to the power of <em>b</em></Cell></Row>
                    <Row><Cell><code>exp(x)</code></Cell><Cell>e raised to the power of <em>x</em></Cell></Row>
                </TBody>
            </Table>

            <h3 className={css.header}>Operators</h3>
            <Table size="small">
                <Head>
                    <HeaderRow>
                        <HeaderCell>Operator</HeaderCell>
                        <HeaderCell>Description</HeaderCell>
                        <HeaderCell>Examples</HeaderCell>
                    </HeaderRow>
                </Head>
                <TBody>
                    <Row><Cell><code>+  -  *  /</code></Cell><Cell>Basic arithmetic</Cell><Cell><code>3 + 2 = 5</code>, <code>10 / 4 = 2.5</code></Cell></Row>
                    <Row><Cell><code>^</code> or <code>**</code></Cell><Cell>Exponentiation</Cell><Cell><code>2 ^ 3 = 8</code></Cell></Row>
                    <Row><Cell><code>%</code></Cell><Cell>Modulo</Cell><Cell><code>10 % 3 = 1</code></Cell></Row>
                    <Row><Cell><code>==  !=  &gt;  &lt;  &gt;=  &lt;=</code></Cell><Cell>Comparison operators</Cell><Cell><code>5 &gt; 3</code> is true, <code>2 != 2</code> is false</Cell></Row>
                    <Row><Cell><code>and  or  not</code></Cell><Cell>Logical operators</Cell><Cell><code>true and false</code> is false,  <code>true or false</code> is true</Cell></Row>
                </TBody>
            </Table>
        </>
    );
}

export function FormulaField(props: FormulaInputProps): ReactElement {
    const [helpOpen, setHelpOpen] = useState(false);
    const [editorOpen, setEditorOpen] = useState(false);

    function doCloseEditor() {
        if(props.validateFormula) {
            props.validateFormula(props.value)
                .then(res => {
                    if(!res.valid) {
                        toast.error(res.error ?? "Invalid formula");
                    } else {
                        setEditorOpen(false);
                    }
                });
        } else {
            setEditorOpen(false);
        }
    }

    return (
        <>
        <Field className={props.className} style={{width: "15rem"}}>
            {props.label ? props.label : <Label hidden>Formula</Label>}
            <InputGroup>
                <Input
                    disabled={props.disabled}
                    value={props.value}
                    type="text"
                    onChange={props.onChange}
                    placeholder={props.placeholder ?? "Enter custom formula"}
                />
                <Button
                    focusInset
                    isNeutral
                    aria-label="Open larger formula editor"
                    onClick={() => setEditorOpen(true)}
                    disabled={props.disabled}
                >
                    <MaximizeIcon />
                </Button>
            </InputGroup>
            {helpOpen &&
                <Modal isLarge onClose={() => setHelpOpen(false)}>
                    <Header>Formula Help</Header>
                    <Body>
                        <FormulaHelpContent />
                    </Body>
                    <Close aria-label="Close modal" />
                </Modal>
            }
            {editorOpen &&
                <Modal isLarge onClose={() => doCloseEditor()}>
                    <Header>Formula Editor</Header>
                    <Body>
                        <AutocompleteTextarea
                            keywords={FORMULA_KEYWORDS}
                            value={props.value}
                            onChange={props.onChange}
                            placeholder={props.placeholder ?? "Enter custom formula"}
                            rows={6}
                            style={{fontFamily: "monospace"}}
                        />
                        <div style={{maxHeight: "200px", overflowY: "auto", marginTop: "1rem"}}>
                            <FormulaHelpContent />
                        </div>
                    </Body>
                    <Footer>
                        <FooterItem>
                            <Button onClick={() => doCloseEditor()} isPrimary>
                                Done
                            </Button>
                        </FooterItem>
                    </Footer>
                    <Close aria-label="Close modal" />
                </Modal>
            }
        </Field>
        <IconButton
            isBasic
            className={css.help}
            size="small"
            disabled={props.disabled}
            onClick={() => setHelpOpen(true)}
            aria-label="Formula help"
        >
            <QuestionMarkIcon />
        </IconButton>
    </>
    );
}
